import { apiService } from './apiService';
import { config } from '../config/environment';
import { isAllowedSyncInterval } from './rmmSyncSchedule';

// ── Types (mirror aitryx-core RMM DTOs; camelCase) ──────────────────────────

export interface RmmConnectionTestResult {
  success: boolean;
  latencyMs: number;
  message?: string;
  error?: string;
  statusCode?: number;
  testedAt: string;
}

export interface NinjaRmmConfig {
  connected: boolean;
  provider: string;
  region?: string;
  clientId?: string;
  hasSecret: boolean;
  displayName?: string;
  status: string; // Active | Disabled | Error | NotConnected
  connectedAt?: string;
  lastTestedAt?: string;
  lastTestResult?: RmmConnectionTestResult | null;
  lastSyncedAt?: string;
  /**
   * Hours between automatic device pulls. Present (including null) only when this
   * Core build can store the interval. Omitted by older Core builds.
   */
  syncIntervalHours?: number | null;
  /** When Core reports the next scheduled pull. */
  nextSyncAt?: string | null;
}

export interface SaveNinjaRmmConfigRequest {
  region: string;
  clientId: string;
  clientSecret?: string; // omit / "***configured***" keeps the stored secret
  displayName?: string;
}

export interface TestNinjaRmmConfigRequest {
  region?: string;
  clientId?: string;
  clientSecret?: string;
}

export interface RmmOrgMappingView {
  externalOrgId: string;
  externalOrgName: string;
  mappedCustomerId?: string | null;
  mappedCustomerName?: string | null;
  suggestedCustomerId?: string | null;
  suggestedCustomerName?: string | null;
  matchConfidence: string; // auto-high | auto-low | manual | unmapped
  status: string;          // Suggested | Confirmed | Ignored
  deviceCount: number;
}

export interface RmmMappingInput {
  externalOrgId: string;
  externalOrgName?: string;
  customerId?: string | null;
  status: string; // Confirmed | Ignored
}

export interface RmmSyncResult {
  success: boolean;
  organizationCount: number;
  deviceCount: number;
  devicesWithSerial: number;
  syncedAt: string;
  durationMs: number;
  error?: string;
}

export interface RmmReconciliationDevice {
  hostName?: string | null;
  serialNumber?: string | null;
  model?: string | null;
  /** Windows/Ninja last logged-on user (Domain\User), as of last sync. Not an Aitryx sign-in. */
  lastLoggedOnUser?: string | null;
  lastContactUtc?: string | null;
  aitryxLastSeenUtc?: string | null;
  status?: string | null;
  aitryxProductName?: string | null;
  aitryxProductVersion?: string | null;
  aitryxInstalledAtUtc?: string | null;
  softwareChecked?: boolean;
  aitryxAgentVersion?: string | null;
  aitryxUserName?: string | null;
  aitryxUserEmail?: string | null;
}

export interface RmmReconciliationCustomer {
  externalOrgId: string;
  externalOrgName: string;
  customerId: string;
  customerName: string;
  ninjaCount: number;
  aitryxCount: number;
  matchedCount: number;
  installGapCount: number;
  staleCount: number;
  ninjaStaleCount?: number;
  notInstalledCount?: number;
  installedNotSignedInCount?: number;
  agentQuietCount?: number;
  healthyCount?: number;
  installUnknownCount?: number;
  aitryxOnlyCount?: number;
  aitryxOnly?: RmmReconciliationDevice[];
  installGap: RmmReconciliationDevice[];
  stale: RmmReconciliationDevice[];
  ninjaStale?: RmmReconciliationDevice[];
  notInstalled?: RmmReconciliationDevice[];
  installedNotSignedIn?: RmmReconciliationDevice[];
  agentQuiet?: RmmReconciliationDevice[];
  healthy?: RmmReconciliationDevice[];
  installUnknown?: RmmReconciliationDevice[];
  error?: string | null;
}

export interface RmmCustomerReconciliation {
  connected: boolean;
  mapped: boolean;
  customer?: RmmReconciliationCustomer | null;
  lastSyncedAt?: string | null;
}

export interface RmmReconciliationResult {
  customers: RmmReconciliationCustomer[];
  totalNinja: number;
  totalAitryx: number;
  totalInstallGap: number;
  totalStale: number;
  totalNinjaStale?: number;
  totalNotInstalled?: number;
  totalInstalledNotSignedIn?: number;
  totalAgentQuiet?: number;
  totalHealthy?: number;
  totalInstallUnknown?: number;
  unmappedOrgCount: number;
  lastSyncedAt?: string | null;
  generatedAt: string;
}

// ── Service ─────────────────────────────────────────────────────────────────
// Uses apiService.http (the shared axios client) directly: it carries the core
// JWT auth + 401-retry + error interceptors, but returns the bare response body
// (the RMM endpoints return plain objects, not the {success,data} envelope).

const base = (partnerId: string) => `/partner/${encodeURIComponent(partnerId)}/rmm/ninjaone`;

/** A full device-list pull can page through every NinjaOne organization. */
const DEVICE_SYNC_TIMEOUT_MS = 300_000;

/**
 * Local stand-in so the Ninja page can be exercised without Azure AD.
 * Production builds never take this path: dev mode also requires localhost.
 */
let devNinjaConfig: NinjaRmmConfig = {
  connected: true,
  provider: 'ninjaone',
  region: 'oc',
  clientId: 'dev-client-id',
  hasSecret: true,
  displayName: 'Dev NinjaOne',
  status: 'Active',
  connectedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  lastTestedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  lastTestResult: null,
  lastSyncedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
};

function devConfigCopy(): NinjaRmmConfig {
  return { ...devNinjaConfig };
}

function devReconciliationCustomer(customerId: string, customerName: string): RmmReconciliationCustomer {
  const contact = new Date().toISOString();
  const oldContact = new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString();
  const quiet = new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString();
  const recent = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
  const ninjaStale: RmmReconciliationDevice[] = [{
    status: 'NinjaStale',
    hostName: 'ACME-OFF-01',
    serialNumber: 'SN-STALE',
    model: 'ThinkPad',
    lastLoggedOnUser: 'AzureAD\\OldUser',
    lastContactUtc: oldContact,
    softwareChecked: true,
    aitryxProductName: 'Aitryx',
    aitryxProductVersion: '1.2.2',
    aitryxLastSeenUtc: oldContact,
    aitryxAgentVersion: '1.2.2',
    aitryxUserName: 'Old User',
  }];
  const notInstalled: RmmReconciliationDevice[] = [{
    status: 'NotInstalled',
    hostName: 'ACME-NEW-02',
    serialNumber: 'SN-NEW',
    model: 'Latitude',
    lastLoggedOnUser: 'AzureAD\\NewUser',
    lastContactUtc: contact,
    softwareChecked: true,
  }];
  const installedNotSignedIn: RmmReconciliationDevice[] = [{
    status: 'InstalledNotSignedIn',
    hostName: 'STAR-PW0NK4NC',
    serialNumber: 'PW0NK4NC',
    model: '21V0',
    lastLoggedOnUser: 'AzureAD\\PradeepPariyarth',
    lastContactUtc: contact,
    softwareChecked: true,
    aitryxProductName: 'Aitryx',
    aitryxProductVersion: '1.2.4',
  }];
  const agentQuiet: RmmReconciliationDevice[] = [{
    status: 'AgentQuiet',
    hostName: 'ACME-QUIET-03',
    serialNumber: 'SN-QUIET',
    model: 'EliteBook',
    lastLoggedOnUser: 'AzureAD\\QuietUser',
    lastContactUtc: contact,
    softwareChecked: true,
    aitryxProductName: 'Aitryx',
    aitryxProductVersion: '1.2.4',
    aitryxLastSeenUtc: quiet,
    aitryxAgentVersion: '1.2.3',
    aitryxUserEmail: 'quiet@acme.test',
  }];
  const healthy: RmmReconciliationDevice[] = [{
    status: 'Healthy',
    hostName: 'ACME-OK-04',
    serialNumber: 'SN-OK',
    model: 'ProBook',
    lastLoggedOnUser: 'AzureAD\\CurrentUser',
    lastContactUtc: contact,
    softwareChecked: true,
    aitryxProductName: 'Aitryx',
    aitryxProductVersion: '1.2.4',
    aitryxLastSeenUtc: recent,
    aitryxAgentVersion: '1.2.4',
    aitryxUserName: 'Current User',
    aitryxUserEmail: 'current@acme.test',
  }];
  return {
    externalOrgId: 'dev-org-1',
    externalOrgName: 'Acme Corporation',
    customerId,
    customerName,
    ninjaCount: 5,
    aitryxCount: 4,
    matchedCount: 3,
    installGapCount: 2,
    staleCount: 1,
    ninjaStaleCount: 1,
    notInstalledCount: 1,
    installedNotSignedInCount: 1,
    agentQuietCount: 1,
    healthyCount: 1,
    installUnknownCount: 0,
    aitryxOnlyCount: 1,
    aitryxOnly: [{
      status: 'AitryxOnly',
      hostName: 'ACME-ONLY-09',
      serialNumber: 'SN-ONLY',
      model: 'Latitude',
      aitryxLastSeenUtc: recent,
      aitryxAgentVersion: '1.2.4',
      aitryxUserName: 'Outside Ninja',
      aitryxUserEmail: 'outside@acme.test',
    }],
    installGap: [...notInstalled, ...installedNotSignedIn],
    stale: agentQuiet,
    ninjaStale,
    notInstalled,
    installedNotSignedIn,
    agentQuiet,
    healthy,
    installUnknown: [],
  };
}

export class RmmService {
  async getConfig(partnerId: string): Promise<NinjaRmmConfig> {
    if (config.features.devMode) return devConfigCopy();
    const res = await apiService.http.get<NinjaRmmConfig>(`${base(partnerId)}/config`);
    return res.data;
  }

  async saveConfig(partnerId: string, body: SaveNinjaRmmConfigRequest): Promise<NinjaRmmConfig> {
    const res = await apiService.http.put<NinjaRmmConfig>(`${base(partnerId)}/config`, body);
    return res.data;
  }

  async testConnection(partnerId: string, body?: TestNinjaRmmConfigRequest): Promise<RmmConnectionTestResult> {
    const res = await apiService.http.post<RmmConnectionTestResult>(`${base(partnerId)}/test`, body ?? {});
    return res.data;
  }

  async disconnect(partnerId: string): Promise<void> {
    await apiService.http.delete(`${base(partnerId)}/config`);
  }

  async getOrganizations(partnerId: string): Promise<RmmOrgMappingView[]> {
    if (config.features.devMode) {
      return [
        {
          externalOrgId: 'dev-org-1',
          externalOrgName: 'Acme Corporation',
          mappedCustomerId: 'mock-customer-1',
          mappedCustomerName: 'Acme Corporation',
          matchConfidence: 'manual',
          status: 'Confirmed',
          deviceCount: 12,
        },
      ];
    }
    const res = await apiService.http.get<RmmOrgMappingView[]>(`${base(partnerId)}/organizations`);
    return res.data;
  }

  async saveMappings(partnerId: string, mappings: RmmMappingInput[]): Promise<void> {
    await apiService.http.put(`${base(partnerId)}/mappings`, { mappings });
  }

  async sync(partnerId: string): Promise<RmmSyncResult> {
    if (config.features.devMode) {
      const syncedAt = new Date().toISOString();
      devNinjaConfig = { ...devNinjaConfig, lastSyncedAt: syncedAt };
      return {
        success: true,
        organizationCount: 1,
        deviceCount: 12,
        devicesWithSerial: 10,
        syncedAt,
        durationMs: 40,
      };
    }
    const res = await apiService.http.post<RmmSyncResult>(
      `${base(partnerId)}/sync`,
      {},
      { timeout: DEVICE_SYNC_TIMEOUT_MS },
    );
    return res.data;
  }

  /**
   * Saves how often Core should pull the device list.
   * Call this only when GET config includes `syncIntervalHours` (older Core omits it
   * and does not have this route). Allowed hours: 6, 12, 24, 168.
   */
  async updateSyncInterval(partnerId: string, syncIntervalHours: number): Promise<NinjaRmmConfig> {
    if (!isAllowedSyncInterval(syncIntervalHours)) {
      throw new Error('Choose a 6 hour, 12 hour, daily, or weekly device pull.');
    }
    if (config.features.devMode) {
      devNinjaConfig = { ...devNinjaConfig, syncIntervalHours };
      return devConfigCopy();
    }
    const res = await apiService.http.put<NinjaRmmConfig>(`${base(partnerId)}/sync-schedule`, {
      syncIntervalHours,
    });
    return res.data;
  }

  async getReconciliation(partnerId: string): Promise<RmmReconciliationResult> {
    if (config.features.devMode) {
      const cfg = devConfigCopy();
      return {
        customers: [devReconciliationCustomer('mock-customer-1', 'Acme Corporation')],
        totalNinja: 5,
        totalAitryx: 4,
        totalInstallGap: 2,
        totalStale: 1,
        totalNinjaStale: 1,
        totalNotInstalled: 1,
        totalInstalledNotSignedIn: 1,
        totalAgentQuiet: 1,
        totalHealthy: 1,
        totalInstallUnknown: 0,
        unmappedOrgCount: 0,
        lastSyncedAt: cfg.lastSyncedAt,
        generatedAt: new Date().toISOString(),
      };
    }
    const res = await apiService.http.get<RmmReconciliationResult>(`${base(partnerId)}/reconciliation`);
    return res.data;
  }

  async getCustomerReconciliation(partnerId: string, customerId: string): Promise<RmmCustomerReconciliation> {
    if (config.features.devMode) {
      const cfg = devConfigCopy();
      return {
        connected: true,
        mapped: true,
        lastSyncedAt: cfg.lastSyncedAt,
        customer: devReconciliationCustomer(customerId, 'Acme Corporation'),
      };
    }
    const res = await apiService.http.get<RmmCustomerReconciliation>(
      `${base(partnerId)}/reconciliation/customer/${encodeURIComponent(customerId)}`
    );
    return res.data;
  }

  /** Download the full per-customer reconciliation CSV (authenticated blob fetch). */
  async downloadCustomerReconciliationCsv(partnerId: string, customerId: string, customerName?: string): Promise<void> {
    const blob = await apiService.getBlob(
      `${base(partnerId)}/reconciliation/customer/${encodeURIComponent(customerId)}/export`
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const slug = (customerName || customerId).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    link.download = `rmm-reconciliation-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export const rmmService = new RmmService();
export default rmmService;
